import React, { useEffect, useMemo, useState } from "react";
import { Camera } from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const fallbackItems = [
  {
    title: "Fresh mowing lines",
    category: "Lawn Mowing",
    description: "Real job photos will be added soon.",
  },
  {
    title: "Mulch bed refresh",
    category: "Mulching",
    description: "Real job photos will be added soon.",
  },
  {
    title: "Shrub trimming cleanup",
    category: "Trimming",
    description: "Real job photos will be added soon.",
  },
  {
    title: "Spring cleanup",
    category: "Seasonal Cleanup",
    description: "Real job photos will be added soon.",
  },
  {
    title: "Fall cleanup",
    category: "Seasonal Cleanup",
    description: "Real job photos will be added soon.",
  },
  {
    title: "Tree limb cleanup",
    category: "Tree Work",
    description: "Real job photos will be added soon.",
  },
];

function getPublicImageUrl(path) {
  if (!path) return "";

  const { data } = supabase.storage.from("work-photos").getPublicUrl(path);
  return data.publicUrl;
}

export default function PublicWorkGallery({ SiteBackground }) {
  const [gallery, setGallery] = useState([]);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    async function loadGallery() {
      setStatus("loading");

      try {
        const { data, error } = await supabase
          .from("work_gallery")
          .select("*")
          .eq("is_visible", true)
          .order("display_order", { ascending: true })
          .order("created_at", { ascending: false });

        if (error) throw error;

        setGallery(data || []);
        setStatus("success");
      } catch (error) {
        console.error("Work gallery failed to load:", error);
        setStatus("error");
      }
    }

    loadGallery();
  }, []);

  const hasRealPhotos = gallery.length > 0;

  const visibleItems = useMemo(() => {
    if (hasRealPhotos) return gallery;
    return fallbackItems;
  }, [gallery, hasRealPhotos]);

  return (
    <section
      id="work"
      className="relative isolate scroll-mt-24 overflow-hidden bg-slate-950 px-5 py-20 text-white lg:px-8"
    >
      <SiteBackground bottomGrass={false} className="opacity-90" />

      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.3em] text-lime-300">
              Previous work
            </p>

            <h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">
              {hasRealPhotos
                ? "Real work photos from completed jobs."
                : "Work gallery coming soon."}
            </h2>
          </div>

          <p className="max-w-2xl text-lg leading-8 text-slate-300 lg:justify-self-end">
            {hasRealPhotos
              ? "Browse recent work from Precision Lawn Workx LLC. More project photos will be added as jobs are completed."
              : "Precision Lawn Workx LLC is gathering real job photos. Until then, here are the main results customers can request."}
          </p>
        </div>

        {status === "loading" && (
          <div className="mt-10 rounded-[2rem] border border-white/10 bg-white/[0.06] p-6 font-black text-slate-300">
            Loading work gallery...
          </div>
        )}

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleItems.map((item, index) => {
            const imageUrl = getPublicImageUrl(item.image_path);

            return (
              <div
                key={item.id || item.title}
                className="group overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.06] p-3 shadow-2xl shadow-black/20 backdrop-blur"
              >
                <div className="relative grid aspect-[4/3] place-items-center overflow-hidden rounded-[1.45rem] bg-gradient-to-br from-green-900 via-slate-900 to-black">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={item.title}
                      className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <>
                      <div className="absolute inset-0 lawn-stripes opacity-50" />
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/30" />

                      <div className="relative text-center">
                        <Camera className="mx-auto h-10 w-10 text-lime-200" />
                        <p className="mt-3 text-sm font-black uppercase tracking-[0.25em] text-slate-400">
                          Result {index + 1}
                        </p>
                      </div>
                    </>
                  )}

                  {imageUrl && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-lime-200">
                        {item.category || "Work Photo"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-lime-300">
                    {item.category || "Precision Work"}
                  </p>

                  <h3 className="mt-2 text-xl font-black">{item.title}</h3>

                  <p className="mt-2 text-sm leading-6 text-slate-400">
                    {item.description || "Real project photo from Precision Lawn Workx LLC."}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}